import {
  SkyButton,
  SkyDateRangePicker,
  SkyDropdown,
  SkyInput,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useId } from "react";
import { AUDIT_ACTIONS, type AuditAction } from "@shared/audit/actions";
import { auditActionOptionText } from "@/components/audit/actionLabels";
import type { AuditFilters as AuditFilterValues } from "@/hooks/use-audit";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { dayBoundsInZone } from "@/lib/time/displayTime";

/**
 * Actor, action, entity and date over the trail (canvas 09, "Filters").
 *
 * Actor is a free-text field rather than a picker: the rows store an HMAC of the
 * address, never the address (SEC-13), so there is no list of actors to offer.
 * The route hashes what is typed the same way the row was written, which is how a
 * reviewer searches by address without the address being stored anywhere.
 *
 * The entity types are the ones the closed action set can produce, so the field
 * is a dropdown rather than another free-text box a typo turns into an empty page.
 *
 * Round 6 T-04 (platform 0027 folded customers into tenants): neither dropdown says Customer.
 * The entity "tenant" also finds the historical `customer` rows (`auditConditions` in
 * `server/routes/audit.ts` widens it), as the grid already shows those rows on entity "tenant",
 * and the two historical actions read as their grid words with "(history)" while the value sent
 * is still the stored action. Walk of 9a9d586 (D-c): every action the grids name in words is
 * offered in those words (`auditActionOptionText`), the runner's own rows included.
 */

const ENTITY_TYPES = [
  "role_assignment",
  "user",
  "instance",
  "instance_binding",
  "instance_settings",
  "dataflow",
  "schedule",
  "run",
  "run_step",
  "connector_definition",
  "tenant",
  // Round 6 T-29: also finds the historical `transformation` rows (`server/routes/audit.ts`).
  "dbt_job",
  "platform_setting",
  "secret",
  "session",
] as const;

/**
 * Under the filter row, not under the field (walk W20), on both trails: a field's hint moved
 * Actor off the row's baseline. Still the Actor field's description (`aria-describedby`).
 */
export const ACTOR_HELP = "Actor is searched by the same hash the row was written with.";

/**
 * A picked day's first instant in the display zone (B-22, round 9). The picker hands back the day
 * as a Date at this device's midnight, so its own calendar fields name the day; it is never
 * mutated, and the bound is that day's midnight in the zone the grid shows times in.
 */
export function toIsoStart(date: Date | null, zone: string): string | undefined {
  return date
    ? dayBoundsInZone(
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
        zone,
      ).start.toISOString()
    : undefined;
}

/** A picked day's last instant (23:59:59.999) in the display zone. */
export function toIsoEnd(date: Date | null, zone: string): string | undefined {
  return date
    ? dayBoundsInZone(
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
        zone,
      ).end.toISOString()
    : undefined;
}

export function AuditFilters({
  filters,
  showActionFilters,
  onChange,
  onClear,
  offerMcpActions = true,
}: {
  filters: AuditFilterValues;
  /** A saved report chooses its own actions and entities, so those two go away. */
  showActionFilters: boolean;
  /** `features.mcp` (`useMcpEnabled`): false, the AI app actions are not offered (#1812). */
  offerMcpActions?: boolean;
  onChange: (next: AuditFilterValues) => void;
  onClear: () => void;
}) {
  const actorHelpId = useId();
  const zone = useDisplayTz();
  // #1812: AI apps off, their actions are not offered as filters. Past rows keep their labels
  // (a record), and an action already chosen, from a shared link, stays in the list.
  const actions = AUDIT_ACTIONS.filter(
    (action) => offerMcpActions || !action.startsWith("mcp.") || action === filters.action,
  );
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-end gap-3" data-testid="audit-filters">
        <div className="w-64">
          {/* No hint under the field: it pushed Actor off the row's baseline (walk W20). The
              help is the line under the row, and still the field's description. */}
          <SkyInput
            label="Actor"
            type="search"
            placeholder="Email address or user id"
            value={filters.actor ?? ""}
            testId="audit-actor"
            aria-describedby={actorHelpId}
            onChange={(event) => onChange({ ...filters, actor: event.target.value })}
          />
        </div>

        {showActionFilters ? (
          <>
            <div className="w-64">
              <SkyDropdown
                label="Action"
                testId="audit-action"
                selectedOptions={[filters.action ?? ""]}
                listboxMaxHeight="320px"
                options={[
                  { value: "", text: "Any action" },
                  ...actions.map((action) => ({
                    value: action,
                    text: auditActionOptionText(action),
                  })),
                ]}
                onOptionSelect={(values) =>
                  onChange({ ...filters, action: (values[0] ?? "") as AuditAction | "" })
                }
              />
            </div>
            <div className="w-56">
              <SkyDropdown
                label="Entity type"
                testId="audit-entity-type"
                selectedOptions={[filters.entityType ?? ""]}
                options={[
                  { value: "", text: "Any entity" },
                  ...ENTITY_TYPES.map((entity) => ({ value: entity, text: entity })),
                ]}
                onOptionSelect={(values) => onChange({ ...filters, entityType: values[0] ?? "" })}
              />
            </div>
          </>
        ) : null}

        <div className="w-72">
          <SkyDateRangePicker
            label="Occurred between"
            testId="audit-date-range"
            onSelectDateRange={(range) =>
              onChange({
                ...filters,
                from: toIsoStart(range.startDate, zone),
                to: toIsoEnd(range.endDate, zone),
              })
            }
          />
        </div>

        <SkyButton appearance="subtle" size="small" testId="audit-clear-filters" onClick={onClear}>
          Clear filters
        </SkyButton>
      </div>
      <SkyText id={actorHelpId} size={200} color="muted" block testId="audit-actor-help">
        {ACTOR_HELP}
      </SkyText>
    </div>
  );
}
